import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  X,
  RefreshCw,
  Plus,
  Loader2,
  Upload,
  FileText,
  Pencil,
  Printer,
} from 'lucide-react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import {
  fetchShopSettlements,
  fetchShopSettlement,
  fetchPendingShopOrders,
  createShopSettlement,
} from '../services/shopSettlementService';
import { fetchShops } from '../services/shopService';
import { uploadFile } from '../services/uploadService';
import ShopSettlementEditModal from '../components/ShopSettlementEditModal';
import { openShopSettlementPrintWindow } from '../utils/shopSettlementPrint';

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

const formatAmount = (value) => {
  const amount = Number.parseFloat(value);
  if (Number.isNaN(amount)) {
    return currencyFormatter.format(0);
  }
  return currencyFormatter.format(amount);
};

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString();
};

const todayDateValue = () => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const PAYMENT_STATUS_LABELS = {
  0: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
  1: { label: 'Paid', className: 'bg-green-100 text-green-700' },
};

const EMPTY_TOTALS = {
  totalOrders: 0,
  totalAmount: 0,
  commissionPercentage: 0,
  commissionAmount: 0,
  payableAmount: 0,
};

function CreateShopSettlementModal({ isOpen, onClose, onCreated }) {
  const [shops, setShops] = useState([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const [shopId, setShopId] = useState('');
  const [filters, setFilters] = useState({ fromDate: '', toDate: '' });
  const [shopInfo, setShopInfo] = useState(null);
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userEditedAmount, setUserEditedAmount] = useState(false);
  const [commissionPercentageInput, setCommissionPercentageInput] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [settlementDate, setSettlementDate] = useState(todayDateValue());
  const [note, setNote] = useState('');
  const [signatureUrl, setSignatureUrl] = useState('');
  const [signatureUploading, setSignatureUploading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setShopId('');
    setFilters({ fromDate: '', toDate: '' });
    setShopInfo(null);
    setOrders([]);
    setTotals(EMPTY_TOTALS);
    setSelectedOrderIds([]);
    setAmountPaid('');
    setSettlementDate(todayDateValue());
    setNote('');
    setSignatureUrl('');
    setUserEditedAmount(false);
    setCommissionPercentageInput('');

    const loadShops = async () => {
      setLoadingShops(true);
      try {
        const response = await fetchShops({ page: 1, limit: 200 });
        const records = response.data?.data?.records || [];
        setShops(records);
      } catch (error) {
        console.error(error);
        toast.error('Failed to load shops');
      } finally {
        setLoadingShops(false);
      }
    };

    loadShops();
  }, [isOpen]);

  const commissionRate = useMemo(() => {
    const parsed = Number.parseFloat(commissionPercentageInput);
    if (Number.isNaN(parsed) || parsed < 0) {
      return 0;
    }
    return parsed;
  }, [commissionPercentageInput]);

  const pendingSummary = useMemo(() => {
    const totalAmount = Number.parseFloat(totals.totalAmount || 0) || 0;
    const commissionAmount = Number(((totalAmount * commissionRate) / 100).toFixed(2));
    const payableAmount = Number((totalAmount - commissionAmount).toFixed(2));
    return {
      totalAmount,
      commissionAmount,
      payableAmount,
    };
  }, [commissionRate, totals.totalAmount]);

  const fetchOrders = useCallback(async () => {
    if (!isOpen) {
      return;
    }

    if (!shopId) {
      setShopInfo(null);
      setOrders([]);
      setTotals(EMPTY_TOTALS);
      setSelectedOrderIds([]);
      setAmountPaid('');
      setUserEditedAmount(false);
      setCommissionPercentageInput('');
      return;
    }

    setLoadingOrders(true);
    try {
      const params = { shopId };
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      const response = await fetchPendingShopOrders(params);
      const data = response.data?.data || {};
      const pendingOrders = data.orders || [];
      setShopInfo(data.shop || null);
      setOrders(pendingOrders);
      setTotals({
        totalOrders: data.totals?.totalOrders ?? pendingOrders.length,
        totalAmount: Number.parseFloat(data.totals?.totalAmount ?? 0) || 0,
        commissionPercentage: Number.parseFloat(data.totals?.commissionPercentage ?? 0) || 0,
        commissionAmount: Number.parseFloat(data.totals?.commissionAmount ?? 0) || 0,
        payableAmount: Number.parseFloat(data.totals?.payableAmount ?? 0) || 0,
      });
      setSelectedOrderIds(pendingOrders.map((order) => order.orderId));
      setUserEditedAmount(false);
      const resolvedCommission =
        data.shop?.commissionPercentage ?? data.totals?.commissionPercentage ?? '';
      const normalizedCommission = Number.parseFloat(resolvedCommission);
      setCommissionPercentageInput(
        Number.isNaN(normalizedCommission) ? '' : `${normalizedCommission}`,
      );
    } catch (error) {
      console.error(error);
      toast.error('Failed to load pending orders');
      setOrders([]);
      setTotals(EMPTY_TOTALS);
      setSelectedOrderIds([]);
      setShopInfo(null);
      setUserEditedAmount(false);
      setCommissionPercentageInput('');
    } finally {
      setLoadingOrders(false);
    }
  }, [filters.fromDate, filters.toDate, isOpen, shopId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const selectedSummary = useMemo(() => {
    const selectedSet = new Set(selectedOrderIds);
    let total = 0;
    orders.forEach((order) => {
      if (!selectedSet.has(order.orderId)) return;
      total += Number.parseFloat(order.finalAmount || 0) || 0;
    });
    const normalizedTotal = Number(total.toFixed(2));
    const commissionAmount = Number(((normalizedTotal * commissionRate) / 100).toFixed(2));
    const payableAmount = Number((normalizedTotal - commissionAmount).toFixed(2));
    return {
      totalAmount: normalizedTotal,
      commissionAmount,
      payableAmount,
    };
  }, [commissionRate, orders, selectedOrderIds]);

  useEffect(() => {
    if (!userEditedAmount) {
      setAmountPaid(
        selectedSummary.payableAmount ? selectedSummary.payableAmount.toFixed(2) : '',
      );
    }
  }, [selectedSummary.payableAmount, userEditedAmount]);

  const balanceAmount = useMemo(() => {
    const paid = Number.parseFloat(amountPaid);
    if (Number.isNaN(paid)) {
      return selectedSummary.payableAmount;
    }
    const balance = Number((selectedSummary.payableAmount - paid).toFixed(2));
    return Math.abs(balance) < 0.01 ? 0 : balance;
  }, [amountPaid, selectedSummary.payableAmount]);

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds((prev) => {
      if (prev.includes(orderId)) {
        const next = prev.filter((id) => id !== orderId);
        if (next.length === 0) {
          setUserEditedAmount(false);
        }
        return next;
      }
      return [...prev, orderId];
    });
  };

  const toggleSelectAll = () => {
    if (orders.length === 0) {
      setSelectedOrderIds([]);
      setUserEditedAmount(false);
      return;
    }

    if (selectedOrderIds.length === orders.length) {
      setSelectedOrderIds([]);
      setUserEditedAmount(false);
    } else {
      setSelectedOrderIds(orders.map((order) => order.orderId));
      setUserEditedAmount(false);
    }
  };

  const handleSignatureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSignatureUploading(true);
    try {
      const response = await uploadFile(file);
      const url = response.data?.data?.fileUrl;
      if (!url) {
        throw new Error('Upload failed');
      }
      setSignatureUrl(url);
      toast.success('Signature uploaded successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to upload signature');
    } finally {
      setSignatureUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!shopId) {
      toast.error('Select a shop');
      return;
    }
    if (selectedOrderIds.length === 0) {
      toast.error('Select at least one order');
      return;
    }
    if (!settlementDate) {
      toast.error('Select a settlement date');
      return;
    }

    const normalizedAmount = Number.parseFloat(amountPaid);
    if (Number.isNaN(normalizedAmount) || normalizedAmount < 0) {
      toast.error('Enter a valid amount to be paid');
      return;
    }

    setSubmitting(true);
    try {
      await createShopSettlement({
        shopId,
        orderIds: selectedOrderIds,
        settlementDate,
        amountPaid: normalizedAmount,
        commissionPercentage: commissionRate,
        commissionAmount: selectedSummary.commissionAmount,
        note: note?.trim() || undefined,
        signatureUrl: signatureUrl || undefined,
      });
      toast.success('Shop settlement created successfully');
      onCreated();
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.message || 'Failed to create settlement';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
      <div className="absolute inset-0" onClick={() => !submitting && onClose()} />
      <div className="relative z-10 w-full max-w-5xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Create Shop Settlement</h2>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto px-6 py-4 space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="shopSelect" className="block text-sm font-medium text-gray-700">
                Shop
              </label>
              <select
                id="shopSelect"
                value={shopId}
                onChange={(event) => {
                  setShopId(event.target.value);
                  setUserEditedAmount(false);
                }}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={loadingShops || submitting}
              >
                <option value="">Select shop</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="shopCommission" className="block text-sm font-medium text-gray-700">
                Commission (%)
              </label>
              <input
                id="shopCommission"
                type="number"
                step="0.01"
                value={commissionPercentageInput}
                onChange={(event) => setCommissionPercentageInput(event.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g. 5"
                disabled={!shopId || loadingOrders || submitting}
              />
              {shopInfo && shopInfo.commissionPercentage !== undefined && shopInfo.commissionPercentage !== null && (
                <p className="mt-1 text-xs text-gray-500">
                  Default: {(Number.parseFloat(shopInfo.commissionPercentage) || 0).toFixed(2)}%
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="shopFromDate" className="block text-sm font-medium text-gray-700">
                  From date
                </label>
                <input
                  id="shopFromDate"
                  type="date"
                  value={filters.fromDate}
                  onChange={(event) => setFilters((prev) => ({ ...prev, fromDate: event.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={loadingOrders || submitting}
                />
              </div>
              <div>
                <label htmlFor="shopToDate" className="block text-sm font-medium text-gray-700">
                  To date
                </label>
                <input
                  id="shopToDate"
                  type="date"
                  value={filters.toDate}
                  onChange={(event) => setFilters((prev) => ({ ...prev, toDate: event.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={loadingOrders || submitting}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-600">
            <div>
              {shopId
                ? `${orders.length} pending order${orders.length === 1 ? '' : 's'} available`
                : 'Select a shop to view pending orders'}
            </div>
            <button
              type="button"
              onClick={fetchOrders}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-100"
              disabled={!shopId || loadingOrders || submitting}
            >
              {loadingOrders ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
              Refresh
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700">Pending summary</h3>
              <dl className="mt-3 space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <dt>Total order amount</dt>
                  <dd className="font-medium">{formatAmount(pendingSummary.totalAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Commission ({commissionRate.toFixed(2)}%)</dt>
                  <dd>{formatAmount(pendingSummary.commissionAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Payable amount</dt>
                  <dd>{formatAmount(pendingSummary.payableAmount)}</dd>
                </div>
              </dl>
            </div>
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
              <h3 className="text-sm font-semibold text-indigo-700">Selected orders</h3>
              <dl className="mt-3 space-y-2 text-sm text-indigo-700">
                <div className="flex justify-between">
                  <dt>Order total</dt>
                  <dd className="font-semibold">{formatAmount(selectedSummary.totalAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Commission ({commissionRate.toFixed(2)}%)</dt>
                  <dd>{formatAmount(selectedSummary.commissionAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Net payable</dt>
                  <dd>{formatAmount(selectedSummary.payableAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Balance amount</dt>
                  <dd>{formatAmount(balanceAmount)}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-2 text-sm">
              <span className="font-medium text-gray-700">Pending orders</span>
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-indigo-600 hover:text-indigo-700"
                disabled={orders.length === 0 || submitting}
              >
                {selectedOrderIds.length === orders.length ? 'Clear selection' : 'Select all'}
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {loadingOrders ? (
                <div className="flex items-center justify-center py-8 text-sm text-gray-500">
                  <Loader2 size={18} className="mr-2 animate-spin" /> Loading orders...
                </div>
              ) : orders.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">
                  No pending orders to settle
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                    <tr>
                      <th className="px-4 py-3">Select</th>
                      <th className="px-4 py-3">Order</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Delivered At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orders.map((order) => {
                      const isSelected = selectedOrderIds.includes(order.orderId);
                      return (
                        <tr key={order.orderId} className={isSelected ? 'bg-indigo-50/60' : ''}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              checked={isSelected}
                              onChange={() => toggleOrderSelection(order.orderId)}
                              disabled={submitting}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{order.showId || order.orderId}</div>
                            <div className="text-xs text-gray-500">
                              {order.customer?.name || '—'}
                              {order.customer?.phone ? ` (${order.customer.phone})` : ''}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-900">{formatAmount(order.finalAmount)}</td>
                          <td className="px-4 py-3 text-gray-600">{formatDateTime(order.deliveredAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="settlementDate" className="block text-sm font-medium text-gray-700">
                Settlement date
              </label>
              <input
                id="settlementDate"
                type="date"
                value={settlementDate}
                onChange={(event) => setSettlementDate(event.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={submitting}
              />
            </div>
            <div>
              <label htmlFor="amountPaid" className="block text-sm font-medium text-gray-700">
                Amount paid to shop
              </label>
              <input
                id="amountPaid"
                type="number"
                step="0.01"
                value={amountPaid}
                onChange={(event) => {
                  setAmountPaid(event.target.value);
                  setUserEditedAmount(true);
                }}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter amount"
                disabled={submitting}
              />
              <p className="mt-1 text-xs text-gray-500">
                Balance amount: {formatAmount(balanceAmount)}
              </p>
            </div>
            <div>
              <label htmlFor="shopSettlementNote" className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <textarea
                id="shopSettlementNote"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
                placeholder="Optional"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Shop signature
              </label>
              <div className="mt-1 flex items-center gap-3">
                <label
                  htmlFor="shopSignatureUpload"
                  className="inline-flex cursor-pointer items-center justify-center space-x-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {signatureUploading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Upload size={16} />
                  )}
                  <span>{signatureUploading ? 'Uploading…' : 'Upload file'}</span>
                  <input
                    id="shopSignatureUpload"
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={handleSignatureUpload}
                    disabled={signatureUploading || submitting}
                  />
                </label>
                {signatureUrl && (
                  <button
                    type="button"
                    onClick={() => setSignatureUrl('')}
                    className="text-sm text-red-600 hover:text-red-700"
                    disabled={submitting || signatureUploading}
                  >
                    Remove
                  </button>
                )}
              </div>
              {signatureUrl && (
                <div className="mt-2 text-sm text-indigo-600">
                  <a href={signatureUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1">
                    <FileText size={14} />
                    <span>View uploaded signature</span>
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={() => !submitting && onClose()}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
              disabled={submitting}
            >
              {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Create settlement
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ShopSettlements() {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState('');
  const [shopFilter, setShopFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState('settlementDate');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [shops, setShops] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [settlementToEdit, setSettlementToEdit] = useState(null);
  const [printingId, setPrintingId] = useState(null);

  useEffect(() => {
    const loadShops = async () => {
      try {
        const response = await fetchShops({ page: 1, limit: 200 });
        const records = response.data?.data?.records || [];
        setShops(records);
      } catch (error) {
        console.error(error);
      }
    };

    loadShops();
  }, []);

  const loadSettlements = async (overrides = {}) => {
    setLoading(true);
    try {
      const params = {
        page: overrides.page ?? page,
        limit: overrides.limit ?? limit,
        sortBy: overrides.sortBy ?? sortBy,
        sortOrder: overrides.sortOrder ?? sortOrder,
        search: overrides.search !== undefined ? overrides.search : search.trim(),
        fromDate: overrides.fromDate !== undefined ? overrides.fromDate : fromDate,
        toDate: overrides.toDate !== undefined ? overrides.toDate : toDate,
        shopId: overrides.shopId !== undefined ? overrides.shopId : shopFilter,
      };

      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === '' || value === null || value === undefined) {
          delete params[key];
        }
      });

      const response = await fetchShopSettlements(params);
      const data = response.data?.data || {};
      setSettlements(data.records || []);
      const meta = data.meta || {};
      setTotalPages(meta.pages || 1);
      setTotalRecords(meta.total || 0);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load shop settlements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sortBy, sortOrder, fromDate, toDate, shopFilter]);

  useEffect(() => {
    if (search === '') {
      setPage(1);
      loadSettlements({ page: 1, search: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmed = search.trim();
    setPage(1);
    loadSettlements({ page: 1, search: trimmed });
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const handleSettlementCreated = () => {
    setShowCreate(false);
    if (page !== 1) {
      setPage(1);
    } else {
      loadSettlements({ page: 1 });
    }
  };

  const openSettlementDetails = async (id) => {
    setShowDetails(true);
    setDetailLoading(true);
    setSelectedSettlement(null);
    try {
      const response = await fetchShopSettlement(id);
      setSelectedSettlement(response.data?.data || null);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch settlement details');
      setShowDetails(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedSettlement(null);
  };

  const openEditModal = (settlement) => {
    setSettlementToEdit(settlement);
    setShowEdit(true);
  };

  const closeEditModal = () => {
    setShowEdit(false);
    setSettlementToEdit(null);
  };

  const handlePrint = async (settlementId, presetData) => {
    setPrintingId(settlementId);
    try {
      const settlement = presetData || (await fetchShopSettlement(settlementId)).data?.data;
      if (!settlement) {
        throw new Error('Settlement not found');
      }
      openShopSettlementPrintWindow(settlement);
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.message || error.message || 'Unable to prepare print view';
      toast.error(message);
    } finally {
      setPrintingId(null);
    }
  };

  const handleSettlementUpdated = async (updated) => {
    await loadSettlements();
    if (updated && updated.id) {
      if (selectedSettlement && selectedSettlement.id === updated.id) {
        setSelectedSettlement(updated);
        if ((updated.paymentStatus || 0) === 1) {
          closeDetails();
        }
      }
    }
    closeEditModal();
  };

  const handleClearFilters = () => {
    setSearch('');
    setShopFilter('');
    setFromDate('');
    setToDate('');
    setSortBy('settlementDate');
    setSortOrder('DESC');
    setPage(1);
    loadSettlements({
      page: 1,
      search: '',
      shopId: '',
      fromDate: '',
      toDate: '',
      sortBy: 'settlementDate',
      sortOrder: 'DESC',
    });
  };

  const startRecord = totalRecords === 0 ? 0 : (page - 1) * limit + 1;
  const endRecord = totalRecords === 0 ? 0 : Math.min(page * limit, totalRecords);

  return (
    <LayoutWithSidebar>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Shop Settlements</h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Manage payouts made to shops and review settlement history.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center justify-center space-x-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              <Plus size={16} />
              <span>Create settlement</span>
            </button>
            <button
              type="button"
              onClick={() => loadSettlements()}
              className="inline-flex items-center justify-center space-x-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <RefreshCw size={16} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <form onSubmit={handleSearchSubmit} className="flex flex-1 items-center space-x-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by shop or notes"
                  className="w-full border rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"
              >
                Search
              </button>
            </form>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center space-x-2">
                <label htmlFor="shopFilter" className="text-sm text-gray-600 dark:text-gray-300">
                  Shop
                </label>
                <select
                  id="shopFilter"
                  value={shopFilter}
                  onChange={(event) => {
                    setShopFilter(event.target.value);
                    setPage(1);
                  }}
                  className="border rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All shops</option>
                  {shops.map((shop) => (
                    <option key={shop.id} value={shop.id}>
                      {shop.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <label htmlFor="shopFromFilter" className="text-sm text-gray-600 dark:text-gray-300">
                  From
                </label>
                <input
                  id="shopFromFilter"
                  type="date"
                  value={fromDate}
                  onChange={(event) => {
                    setFromDate(event.target.value);
                    setPage(1);
                  }}
                  className="border rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="flex items-center space-x-2">
                <label htmlFor="shopToFilter" className="text-sm text-gray-600 dark:text-gray-300">
                  To
                </label>
                <input
                  id="shopToFilter"
                  type="date"
                  value={toDate}
                  onChange={(event) => {
                    setToDate(event.target.value);
                    setPage(1);
                  }}
                  className="border rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex items-center space-x-2 px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <X size={14} />
                <span>Clear</span>
              </button>
            </div>
          </div>

          <div className="relative overflow-x-auto">
            {loading && (
              <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/60 flex items-center justify-center z-10">
                <div className="h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="py-2 px-3">#</th>
                  <th
                    className="py-2 px-3 cursor-pointer"
                    onClick={() => handleSort('settlementDate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Settlement Date</span>
                      {sortBy === 'settlementDate' ? (
                        sortOrder === 'ASC' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                      ) : (
                        <ArrowUpDown size={12} />
                      )}
                    </div>
                  </th>
                  <th className="py-2 px-3">Shop</th>
                  <th className="py-2 px-3">Orders</th>
                  <th
                    className="py-2 px-3 cursor-pointer"
                    onClick={() => handleSort('totalOrderAmount')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Total Amount</span>
                      {sortBy === 'totalOrderAmount' ? (
                        sortOrder === 'ASC' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                      ) : (
                        <ArrowUpDown size={12} />
                      )}
                    </div>
                  </th>
                  <th className="py-2 px-3">Commission %</th>
                  <th className="py-2 px-3">Commission Amount</th>
                  <th
                    className="py-2 px-3 cursor-pointer"
                    onClick={() => handleSort('amountPaid')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Amount Paid</span>
                      {sortBy === 'amountPaid' ? (
                        sortOrder === 'ASC' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                      ) : (
                        <ArrowUpDown size={12} />
                      )}
                    </div>
                  </th>
                  <th className="py-2 px-3">Balance</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {settlements.length === 0 && !loading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="py-6 px-3 text-center text-gray-500 dark:text-gray-300"
                    >
                      No settlements found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  settlements.map((settlement, index) => {
                    const status = PAYMENT_STATUS_LABELS[settlement.paymentStatus || 0] || PAYMENT_STATUS_LABELS[0];
                    return (
                      <tr key={settlement.id} className="border-t border-gray-100 dark:border-gray-700">
                        <td className="py-2 px-3">{(page - 1) * limit + index + 1}</td>
                        <td className="py-2 px-3">{formatDate(settlement.settlementDate)}</td>
                        <td className="py-2 px-3 font-medium">{settlement.shop?.name || '—'}</td>
                        <td className="py-2 px-3">{settlement.orderCount || 0}</td>
                        <td className="py-2 px-3">{formatAmount(settlement.totalOrderAmount)}</td>
                        <td className="py-2 px-3">{Number.parseFloat(settlement.commissionPercentage || 0).toFixed(2)}%</td>
                        <td className="py-2 px-3">{formatAmount(settlement.commissionAmount)}</td>
                        <td className="py-2 px-3">{formatAmount(settlement.amountPaid)}</td>
                        <td className="py-2 px-3">{formatAmount(settlement.balanceAmount)}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${status.className}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={() => openSettlementDetails(settlement.id)}
                              className="inline-flex items-center space-x-1 px-3 py-1 text-sm text-indigo-600 hover:text-indigo-800"
                            >
                              <Eye size={14} />
                              <span>View</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handlePrint(settlement.id)}
                              disabled={printingId === settlement.id}
                              className="inline-flex items-center space-x-1 px-3 py-1 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md disabled:opacity-60"
                            >
                              {printingId === settlement.id ? (
                                <>
                                  <Loader2 size={14} className="animate-spin" />
                                  <span>Preparing...</span>
                                </>
                              ) : (
                                <>
                                  <Printer size={14} />
                                  <span>Reprint</span>
                                </>
                              )}
                            </button>
                            {settlement.paymentStatus !== 1 && (
                              <button
                                type="button"
                                onClick={() => openEditModal(settlement)}
                                className="inline-flex items-center space-x-1 px-3 py-1 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md"
                              >
                                <Pencil size={14} />
                                <span>Edit</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Showing {startRecord} to {endRecord} of {totalRecords} records
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">Rows per page</span>
                <select
                  value={limit}
                  onChange={(event) => {
                    setLimit(Number(event.target.value));
                    setPage(1);
                  }}
                  className="border rounded-md px-2 py-1 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {[10, 20, 50].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  className="px-3 py-1 border rounded disabled:opacity-50 dark:border-gray-600"
                >
                  Prev
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-300">Page {page} of {totalPages}</span>
                <button
                  type="button"
                  disabled={page === totalPages || totalPages === 0}
                  onClick={() => setPage((prev) => prev + 1)}
                  className="px-3 py-1 border rounded disabled:opacity-50 dark:border-gray-600"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateShopSettlementModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={handleSettlementCreated}
        />
      )}

      {showDetails && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center z-40 px-4">
          <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-3xl max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-700">
              <h2 className="text-lg font-semibold">Settlement Details</h2>
              <div className="flex items-center space-x-2">
                {!detailLoading && selectedSettlement && (selectedSettlement.paymentStatus || 0) !== 1 && (
                  <button
                    type="button"
                    onClick={() => openEditModal(selectedSettlement)}
                    className="inline-flex items-center space-x-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Pencil size={14} />
                    <span>Edit</span>
                  </button>
                )}
                {!detailLoading && selectedSettlement && (
                  <button
                    type="button"
                    onClick={() => handlePrint(selectedSettlement.id, selectedSettlement)}
                    disabled={printingId === selectedSettlement.id}
                    className="inline-flex items-center space-x-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    {printingId === selectedSettlement.id ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        <span>Preparing...</span>
                      </>
                    ) : (
                      <>
                        <Printer size={14} />
                        <span>Reprint</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeDetails}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {detailLoading && (
                <div className="flex items-center justify-center py-10">
                  <div className="h-10 w-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {!detailLoading && selectedSettlement && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Shop</p>
                      <p className="font-medium">{selectedSettlement.shop?.name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Settlement date</p>
                      <p className="font-medium">{formatDate(selectedSettlement.settlementDate)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total order amount</p>
                      <p className="font-medium">{formatAmount(selectedSettlement.totalOrderAmount)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Commission</p>
                      <p className="font-medium">
                        {Number.parseFloat(selectedSettlement.commissionPercentage || 0).toFixed(2)}% • {formatAmount(selectedSettlement.commissionAmount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Amount paid</p>
                      <p className="font-medium">{formatAmount(selectedSettlement.amountPaid)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Balance</p>
                      <p className="font-medium">{formatAmount(selectedSettlement.balanceAmount)}</p>
                    </div>
                  </div>

                  {selectedSettlement.note && (
                    <div>
                      <p className="text-sm text-gray-500">Notes</p>
                      <p className="text-sm text-gray-700">{selectedSettlement.note}</p>
                    </div>
                  )}

                  {selectedSettlement.signatureUrl && (
                    <div>
                      <p className="text-sm text-gray-500">Shop signature</p>
                      <div className="mt-2">
                        <a
                          href={selectedSettlement.signatureUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 text-indigo-600 hover:text-indigo-800"
                        >
                          <FileText size={16} />
                          <span>View signature</span>
                        </a>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-sm font-semibold text-gray-700">Orders</h3>
                    <div className="mt-2 max-h-56 overflow-y-auto rounded border border-gray-200">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          <tr>
                            <th className="px-3 py-2">Order</th>
                            <th className="px-3 py-2">Amount</th>
                            <th className="px-3 py-2">Delivered</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {selectedSettlement.orders?.map((entry) => (
                            <tr key={entry.orderId}>
                              <td className="px-3 py-2">{entry.order?.showId || entry.orderId}</td>
                              <td className="px-3 py-2">{formatAmount(entry.order?.finalAmount)}</td>
                              <td className="px-3 py-2">{formatDateTime(entry.order?.deliveredAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showEdit && settlementToEdit && (
        <ShopSettlementEditModal
          isOpen={showEdit}
          settlement={settlementToEdit}
          onClose={closeEditModal}
          onUpdated={handleSettlementUpdated}
        />
      )}
    </LayoutWithSidebar>
  );
}
